// src/config/api.js
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5181/api';

// Rotas que nunca devem carregar um token velho
const AUTH_EXEMPT_PATHS = ['/Auth/login', '/Auth/register', '/Auth/refresh-token'];

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 🔒 CRÍTICO: Envia cookies automaticamente
});

api.interceptors.request.use(
  (config) => {
    const isExempt = AUTH_EXEMPT_PATHS.some((path) => config.url?.includes(path));
    if (!isExempt) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('[API_REQUEST_ERROR]', error);
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let queue = [];

const processQueue = (error, token = null) => {
  queue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  queue = [];
};

const redirectToLogin = () => {
  console.log('[AUTH_REDIRECT] Redirecionando para login');
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('userType');

  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// Retry com backoff exponencial
const retryWithBackoff = async (fn, maxRetries = 2, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      console.warn(`[RETRY] Tentativa ${i + 1}/${maxRetries} falhada, aguardando ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay *= 2; // backoff exponencial
    }
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 🔍 LOG: Erro completo
    console.error('[API_ERROR]', {
      status: error.response?.status,
      url: originalRequest?.url,
      message: error.message,
      data: error.response?.data,
    });

    // 🔍 Se foi o próprio login ou register que falhou, rejeita direto (sem tentar refresh)
    if (originalRequest?.url?.includes('/Auth/login') || originalRequest?.url?.includes('/Auth/register')) {
      console.error('[LOGIN_ERROR] Login/Register falhou com 401');
      return Promise.reject(error);
    }

    // 🔍 Se quem falhou foi o próprio refresh — qualquer erro
    // a sessão morreu de vez
    if (originalRequest?.url?.includes('/Auth/refresh-token')) {
      console.error('[REFRESH_TOKEN_FAILED] Sessão expirada ou inválida');
      redirectToLogin();
      return Promise.reject(error);
    }

    // 🔍 Se não foi 401, ou já foi retentado, rejeita
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // 🔍 Se já está refrescando, enfileira
    if (isRefreshing) {
      console.log('[QUEUE_REQUEST] Requisição enfileirada, aguardando refresh...');
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    console.log('[REFRESH_TOKEN_START] Iniciando refresh do token...');

    try {
      // Retry refresh com backoff
      const { data } = await retryWithBackoff(
        () => api.post('/Auth/refresh-token'),
        2,
        500
      );

      console.log('[REFRESH_TOKEN_SUCCESS] Novo token obtido com sucesso');

      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', data.id);
      localStorage.setItem('userType', data.userType);

      processQueue(null, data.token);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      
      return api(originalRequest);
    } catch (refreshError) {
      console.error('[REFRESH_TOKEN_FAILED_FINAL]', {
        status: refreshError.response?.status,
        message: refreshError.response?.data?.error || refreshError.message,
        code: refreshError.response?.data?.code,
      });

      processQueue(refreshError, null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);